import { edgesOf } from './boxdrawing';
import { isBraille, maskFromBraille } from './braille';

/**
 * The character palette.
 *
 * Ranges are generated rather than hand-listed so the palette covers the whole
 * of Box Drawing and Block Elements, not just the handful of glyphs people
 * usually remember. Names are descriptive (and, for box drawing, derived from
 * the connectivity table) so that search works without shipping the full
 * Unicode name database.
 */

export interface CharInfo {
  char: string;
  codePoint: number;
  name: string;
  category: string;
  keywords: string[];
}

export interface CharCategory {
  id: string;
  name: string;
  chars: string[];
}

function range(from: number, to: number): string[] {
  const out: string[] = [];
  for (let cp = from; cp <= to; cp++) out.push(String.fromCodePoint(cp));
  return out;
}

export const ASCII = range(0x20, 0x7e);
export const LATIN_SUPPLEMENT = range(0xa1, 0xff);
export const BOX_DRAWING = range(0x2500, 0x257f);
export const BLOCK_ELEMENTS = range(0x2580, 0x259f);
export const GEOMETRIC = range(0x25a0, 0x25ff);
export const ARROWS = range(0x2190, 0x21ff);
export const MATH_OPERATORS = range(0x2200, 0x22ff);
export const BRAILLE = range(0x2800, 0x28ff);
export const SYMBOLS = [
  ...range(0x2600, 0x26ff).filter((_, i) => i < 64),
  ...range(0x2700, 0x27bf).filter((_, i) => i < 48),
];
/** Common Powerline / Nerd Font separators. Font support varies. */
export const POWERLINE = ['', '', '', '', '', '', '', '', '', ''];

/* Curated brush sets, matching how people actually draw. */
export const BRUSH_BLOCKS = ['█', '▓', '▒', '░', '▀', '▄', '▌', '▐', '▖', '▗', '▘', '▝', '▙', '▛', '▜', '▟', '▚', '▞', '■', '□', ' '];
export const BRUSH_LIGHT = ['─', '│', '┌', '┐', '└', '┘', '├', '┤', '┬', '┴', '┼'];
export const BRUSH_HEAVY = ['━', '┃', '┏', '┓', '┗', '┛', '┣', '┫', '┳', '┻', '╋'];
export const BRUSH_DOUBLE = ['═', '║', '╔', '╗', '╚', '╝', '╠', '╣', '╦', '╩', '╬'];
export const BRUSH_ROUNDED = ['╭', '╮', '╰', '╯', '─', '│'];
export const BRUSH_DASHED = ['┄', '┅', '┆', '┇', '┈', '┉', '┊', '┋', '╌', '╍', '╎', '╏'];
export const BRUSH_MIXED = ['╞', '╡', '╥', '╨', '╪', '╫', '╼', '╽', '╾', '╿', '┽', '┾', '╀', '╁'];
export const BRUSH_SHADES = ['░', '▒', '▓', '█'];
export const ASCII_RAMPS: Record<string, string> = {
  standard: ' .:-=+*#%@',
  reverse: '@%#*+=-:. ',
  blocks: ' ░▒▓█',
  minimal: ' .*#',
  long: " .'`^\",:;Il!i><~+_-?][}{1)(|\\/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$",
};

export const CATEGORIES: readonly CharCategory[] = [
  { id: 'ascii', name: 'ASCII', chars: ASCII },
  { id: 'blocks', name: 'Block Elements', chars: BLOCK_ELEMENTS },
  { id: 'box', name: 'Box Drawing', chars: BOX_DRAWING },
  { id: 'braille', name: 'Braille', chars: BRAILLE },
  { id: 'geometric', name: 'Geometric Shapes', chars: GEOMETRIC },
  { id: 'arrows', name: 'Arrows', chars: ARROWS },
  { id: 'math', name: 'Mathematical', chars: MATH_OPERATORS },
  { id: 'latin', name: 'Latin Supplement', chars: LATIN_SUPPLEMENT },
  { id: 'symbols', name: 'Symbols', chars: SYMBOLS },
  { id: 'powerline', name: 'Powerline', chars: POWERLINE },
];

const WEIGHT_WORD: Record<number, string> = { 1: 'light', 2: 'heavy', 3: 'double' };

const BLOCK_NAMES: Record<number, string> = {
  0x2580: 'upper half block',
  0x2581: 'lower one eighth block',
  0x2582: 'lower one quarter block',
  0x2583: 'lower three eighths block',
  0x2584: 'lower half block',
  0x2585: 'lower five eighths block',
  0x2586: 'lower three quarters block',
  0x2587: 'lower seven eighths block',
  0x2588: 'full block',
  0x2589: 'left seven eighths block',
  0x258a: 'left three quarters block',
  0x258b: 'left five eighths block',
  0x258c: 'left half block',
  0x258d: 'left three eighths block',
  0x258e: 'left one quarter block',
  0x258f: 'left one eighth block',
  0x2590: 'right half block',
  0x2591: 'light shade',
  0x2592: 'medium shade',
  0x2593: 'dark shade',
  0x2594: 'upper one eighth block',
  0x2595: 'right one eighth block',
  0x2596: 'quadrant lower left',
  0x2597: 'quadrant lower right',
  0x2598: 'quadrant upper left',
  0x2599: 'quadrant upper left and lower left and lower right',
  0x259a: 'quadrant upper left and lower right',
  0x259b: 'quadrant upper left and upper right and lower left',
  0x259c: 'quadrant upper left and upper right and lower right',
  0x259d: 'quadrant upper right',
  0x259e: 'quadrant upper right and lower left',
  0x259f: 'quadrant upper right and lower left and lower right',
};

const NAMED: Record<number, string> = {
  0x2190: 'leftwards arrow',
  0x2191: 'upwards arrow',
  0x2192: 'rightwards arrow',
  0x2193: 'downwards arrow',
  0x2194: 'left right arrow',
  0x2195: 'up down arrow',
  0x21d0: 'leftwards double arrow',
  0x21d2: 'rightwards double arrow',
  0x25a0: 'black square',
  0x25a1: 'white square',
  0x25aa: 'black small square',
  0x25ab: 'white small square',
  0x25b2: 'black up-pointing triangle',
  0x25b6: 'black right-pointing triangle',
  0x25bc: 'black down-pointing triangle',
  0x25c0: 'black left-pointing triangle',
  0x25cf: 'black circle',
  0x25cb: 'white circle',
  0x25d0: 'circle with left half black',
  0x2500: 'box drawings light horizontal',
  0x2502: 'box drawings light vertical',
  0x256d: 'box drawings light arc down and right',
  0x256e: 'box drawings light arc down and left',
  0x256f: 'box drawings light arc up and left',
  0x2570: 'box drawings light arc up and right',
  0xe0b0: 'powerline right solid separator',
  0xe0b1: 'powerline right thin separator',
  0xe0b2: 'powerline left solid separator',
  0xe0b3: 'powerline left thin separator',
};

const DIR_WORDS: Array<['n' | 'e' | 's' | 'w', string]> = [
  ['n', 'up'],
  ['s', 'down'],
  ['w', 'left'],
  ['e', 'right'],
];

/** Descriptive, search-friendly name for a character. */
export function charName(ch: string): string {
  const cp = ch.codePointAt(0) ?? 0;
  if (NAMED[cp]) return NAMED[cp];
  if (cp === 0x20) return 'space';
  if (cp >= 0x21 && cp <= 0x7e) return `ascii ${ch}`;
  if (BLOCK_NAMES[cp]) return BLOCK_NAMES[cp];
  if (isBraille(ch)) {
    const mask = maskFromBraille(ch);
    const dots: number[] = [];
    const order = [1, 2, 3, 7, 4, 5, 6, 8];
    const bits = [0x01, 0x02, 0x04, 0x40, 0x08, 0x10, 0x20, 0x80];
    bits.forEach((bit, i) => {
      if (mask & bit) dots.push(order[i]);
    });
    dots.sort((a, b) => a - b);
    return dots.length ? `braille pattern dots-${dots.join('')}` : 'braille pattern blank';
  }
  if (cp >= 0x2500 && cp <= 0x257f) {
    const edges = edgesOf(ch);
    if (edges) {
      const byWeight = new Map<number, string[]>();
      for (const [dir, word] of DIR_WORDS) {
        const w = edges[dir];
        if (w === 0) continue;
        const list = byWeight.get(w) ?? [];
        list.push(word);
        byWeight.set(w, list);
      }
      const parts = [...byWeight.entries()].map(
        ([w, words]) => `${WEIGHT_WORD[w] ?? 'light'} ${words.join(' and ')}`,
      );
      const dashed = (cp >= 0x2504 && cp <= 0x250b) || (cp >= 0x254c && cp <= 0x254f);
      return `box drawings ${dashed ? 'dashed ' : ''}${parts.join(' and ')}`;
    }
  }
  return `U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
}

export function charInfo(ch: string, category = ''): CharInfo {
  const cp = ch.codePointAt(0) ?? 0;
  const name = charName(ch);
  return {
    char: ch,
    codePoint: cp,
    name,
    category,
    keywords: name.split(/[\s-]+/).filter(Boolean),
  };
}

let index: CharInfo[] | null = null;

function buildIndex(): CharInfo[] {
  if (index) return index;
  const out: CharInfo[] = [];
  const seen = new Set<string>();
  for (const cat of CATEGORIES) {
    for (const ch of cat.chars) {
      if (seen.has(ch)) continue;
      seen.add(ch);
      out.push(charInfo(ch, cat.id));
    }
  }
  index = out;
  return out;
}

/**
 * Search by name, keyword, literal character, or codepoint
 * ("U+2500", "2500", "0x2500").
 */
export function searchChars(query: string, limit = 400): CharInfo[] {
  const all = buildIndex();
  const q = query.trim().toLowerCase();
  if (!q) return all.slice(0, limit);

  const cpMatch = /^(?:u\+|0x)?([0-9a-f]{2,6})$/i.exec(q);
  if (cpMatch) {
    const cp = parseInt(cpMatch[1], 16);
    const ch = String.fromCodePoint(cp);
    const found = all.find((c) => c.codePoint === cp);
    return found ? [found] : [charInfo(ch)];
  }
  if ([...q].length === 1) {
    const found = all.find((c) => c.char === q);
    if (found) return [found];
  }

  const terms = q.split(/\s+/);
  const scored: Array<[number, CharInfo]> = [];
  for (const info of all) {
    let score = 0;
    for (const term of terms) {
      if (info.name.includes(term)) score += info.name.startsWith(term) ? 3 : 2;
      else if (info.keywords.some((k) => k.startsWith(term))) score += 1;
      else {
        score = -1;
        break;
      }
    }
    if (score > 0) scored.push([score, info]);
  }
  scored.sort((a, b) => b[0] - a[0] || a[1].codePoint - b[1].codePoint);
  return scored.slice(0, limit).map(([, info]) => info);
}
